import React, {
	memo,
	useCallback,
	useEffect,
	useMemo,
	useState
} from 'react';
import { useHistory } from 'react-router-dom';

import {
	handleError,
	log
} from '../../../../services/utils';

import {
	getUserList,
	deleteUser
} from '../../../../api/nora/users';

import { useUIAction } from '../../../../contexts/UIContext';
import { useAuthState } from '../../../../contexts/AuthContext';
import {
	useAdminState,
	useAdminAction
} from '../../../../contexts/AdminContext';

import Alert from '../../../Alert';
import EmailAndPhone from '../../../Table/Cells/EmailAndPhone';
import FirstAndLastName from '../../../Table/Cells/FirstAndLastName';
import HelpModal from '../../../Shared/HelpModal';
import MultiSelectColumnFilter from '../../../Table/ColumnFilters/MultiSelectColumnFilter';
import multiSelectFilterFn from '../../../Table/filterFunctions/multiSelectFilterFn';
import PageHeader from '../../../Shared/PageHeader';
import Table from '../../../Table';
import TableRowActions from '../../../Table/Cells/TableRowActions';
import multiWordFilterFn from '../../../Table/filterFunctions/multiWordFilterFn';

const UserList = () => {
	const history = useHistory();

	const {
		user: {
			id,
			permissions
		}
	} = useAuthState();
	const {
		initialized,
		tableState,
		userList
	} = useAdminState();
	const {
		setTableState,
		setUserList
	} = useAdminAction();
	const {
		setApiErrorAlert,
		setInfobar,
		setInfobarLoader
	} = useUIAction();

	const [ isLoading, setIsLoading ] = useState( true );
	const [ deleteConfirm, setDeleteConfirm ] = useState( false );
	const [ userToDelete, setUserToDelete ] = useState( {} );

	useEffect( () => {
		setInfobar( <span>Administration &nbsp;&#9656;&nbsp; Users</span> );
	}, [ setInfobar ] );

	const retrieveUserList = useCallback( async () => {
		setInfobarLoader( 'Retrieving User List...' );

		try {
			const data = await getUserList();

			setUserList( data );
		} catch ( error ) {
			setApiErrorAlert( error );
			throw error;
		}
	}, [
		setApiErrorAlert,
		setInfobarLoader,
		setUserList
	] );

	useEffect( () => {
		( async () => {
			if ( !initialized ) {
				setInfobarLoader( 'Please wait...' );
			} else {
				if ( permissions.listUsers.canExecute && permissions.listUsers.isAvailable ) {
					try {
						await retrieveUserList();
						setInfobarLoader( '' );
					} catch ( error ) {
						if ( error.message !== 'canceled' ) {
							handleError( error );
							setInfobarLoader( '' );
						}
					}
				} else {
					setInfobarLoader( '' );
				}

				setIsLoading( false );
			}
		} )();
	}, [
		initialized,
		permissions,
		retrieveUserList,
		setInfobarLoader
	] );

	const firstAndLastNameCell = useCallback( ( { row } ) => <FirstAndLastName row={ row.original } />, [] );

	const emailAndPhoneCell = useCallback( ( { row } ) => (
		<EmailAndPhone
			email={ row.original.email }
			phone={ row.original.phone }
		/>
	), [] );

	const onView = useCallback( ( { id } ) => {
		history.push( `/administration/users/${ id }` );
	}, [ history ] );

	const onDelete = useCallback( user => {
		setUserToDelete( user );
		setDeleteConfirm( true );
	}, [] );

	const tableRowActionsCell = useCallback( ( { row } ) => {
		const actions = [
			{
				action: onView,
				componentProps: { color: 'pink' },
				disable: !permissions.viewUsers.canExecute,
				icon: 'search',
				permission: permissions.viewUsers.isAvailable,
				text: 'View User'
			},
			{
				action: onDelete,
				componentProps: { negative: true },
				disable: !permissions.deleteUsers.canExecute,
				icon: 'times',
				permission: permissions.deleteUsers.isAvailable && id !== row.original.id,
				text: 'Delete User'
			}
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [
		onView,
		permissions,
		id,
		onDelete
	] );

	const columns = useMemo( () => [
		{
			id: 'name',
			accessor: row => `${ row.firstName }${ row.lastName }`,
			Header: 'Name',
			filter: multiWordFilterFn,
			Cell: firstAndLastNameCell,
			minWidth: 60
		},
		{
			id: 'role',
			accessor: row => `${ row.role }`,
			Header: 'Role',
			Filter: MultiSelectColumnFilter,
			filter: multiSelectFilterFn,
			minWidth: 60
		},
		{
			id: 'organization',
			accessor: row => `${ row.organization }`,
			Header: 'Organization',
			filter: multiWordFilterFn,
			minWidth: 60
		},
		{
			accessor: 'userName',
			Header: 'Username',
			filter: multiWordFilterFn,
			minWidth: 60
		},
		{
			id: 'emailAndPhone',
			accessor: row => `${ row.email } ${ row.phone }`,
			Header: 'Email and Phone',
			filter: multiWordFilterFn,
			Cell: emailAndPhoneCell,
			minWidth: 200
		}
	], [
		emailAndPhoneCell,
		firstAndLastNameCell
	] );

	const actionColumn = useMemo( () => ( {
		id: 'id_actions',
		Cell: tableRowActionsCell,
		disableFilters: true,
		disableResizing: true,
		width: 100,
		minWidth: 100,
		maxWidth: 100
	} ), [ tableRowActionsCell ] );

	const renderColumns = useMemo(
		() => permissions.viewUsers.isAvailable || permissions.deleteUsers.isAvailable
			? [
				...columns,
				actionColumn
			]
			: columns,
		[
			actionColumn,
			columns,
			permissions
		]
	);

	const addUser = useCallback( () => {
		history.push( '/administration/users/0' );
	}, [ history ] );

	const actions = useMemo( () => [
		{
			action: addUser,
			disable: !permissions.addUsers.canExecute,
			icon: 'add',
			permission: permissions.addUsers.isAvailable,
			text: 'Add User'
		}
	], [
		addUser,
		permissions
	] );

	const handleDeleteUser = useCallback( async () => {
		try {
			await deleteUser( userToDelete.id );
			await retrieveUserList();
		} catch ( error ) {
			setApiErrorAlert( error );
		}
	}, [
		userToDelete.id,
		retrieveUserList,
		setApiErrorAlert
	] );

	const deleteConfirmMessage = useMemo( () => <span>Are you sure you would like to delete <strong>{ userToDelete.firstName } { userToDelete.lastName }</strong> from the user list?</span>, [ userToDelete ] );

	const deleteConfirmCloseModal = useCallback( () => setDeleteConfirm( false ), [] );

	if ( isLoading ) {
		return null;
	}

	return (
		<div className="page">
			{ log( 'userAdmin' ) }
			<PageHeader
				actions={ actions }
				title={
					<>
						<span>User Administration</span>
						<HelpModal
							id={ 31 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</>
				}
			/>
			<Table
				columns={ renderColumns }
				data={ userList }
				hasPermission={ permissions.listUsers.canExecute }
				setTableState={ setTableState }
				tableState={ tableState }
			/>
			<Alert
				action={ handleDeleteUser }
				name="user-list-delete-confirm-alert"
				message={ deleteConfirmMessage }
				open={ deleteConfirm }
				closeModal={ deleteConfirmCloseModal }
				type="delete"
			/>
		</div>
	);
};

export default memo( UserList );
